/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import {ColDef} from 'ag-grid-community';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import {getESNChangeEvents} from '@ion/core/src/services/Connect';
import {Device} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {RefreshGridButton} from '@ion/dls/src/components/buttons/RefreshGridButton';
import {
  IonGrid, IonGridAPI, RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {
  PAGE_SIZES
} from '@ion/dls/src/grid/settings';
import {
  useColumnChooser
} from '@ion/dls/src/grid/useColumnChooser';
import {
  useColumnState
} from '@ion/dls/src/grid/useColumnState';
import {ColumnChooserIcon} from '@ion/dls/src/icons/ColumnChooserIcon';
import {DLSTheme} from '@ion/dls/src/types/types';
import {QueryKey} from '@ion/query/src/config/QueryKeys';
import {usePaginatedCTCSearch} from '@ion/query/src/hooks/usePaginatedCTCSearch';
import {EventTypeChooser} from './EventTypeChooser';
import {HealthEventsDiffDetails} from './HealthEventsDiffDetails';
import {HealthEventsRowEditor} from './HealthEventsRowEditor';

export interface HealthEventsTabType {
  tabIndex: Number;
  device?: Device;
  totalRows?: Number;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  flexStyle : {
    display   : 'flex',
    marginTop : '20px'
  },
  dropdownStyle : {
    width  : '250px',
    margin : '0px 10px 10px 0px'
  },
  alignStyle : {
    display        : 'flex',
    justifyContent : 'space-between'
  },
  refresh : {
    marginTop : '10px'
  },
  actions : {
    display   : 'flex',
    marginTop : '20px'
  },
  taskInnerClass : {
    padding : '0px 10px'
  }
}));

export enum EventTypes {
  FAILSAFE_ACTION = 'Failsafe Action Needed',
  RECOVERY_URL = 'Recovery URL',
  ESN = 'ESN 0'
}

export enum EventTypesVal {
  FAILSAFE_ACTION = 0,
  RECOVERY_URL = 1,
  ESN = 2
}

const eventTypeCellRenderer = ({data: {eventTypeId}}) => {
  if (eventTypeId === 0) {
    return EventTypes.FAILSAFE_ACTION;
  } else if (eventTypeId === 1) {
    return EventTypes.RECOVERY_URL;
  }
  return EventTypes.ESN;
};

const diffCountCellRenderer = ({data: {idReportChanges}}) => {
  if (idReportChanges) {
    return Object.keys(idReportChanges?.diffs).length;
  }
};

const allLabel = 'ALL';

/**
 * HealthEventsTab component that displays the Device services
 * data for the selected Device Details tab.
 */
export const HealthEventsTab = ({
  device,
  tabIndex = 0
}: HealthEventsTabType, ref) => {
  const classes                             = useStyles();
  const [diffDetails, setdiffDetails]       = useState<boolean>(true);
  const [refreshGrid, setRefreshGrid]       = useState<boolean>(false);
  const [isLoading, setIsLoading]           = useState<boolean>(false);
  const [viewData, setviewData]             = useState<any>([]);
  const [selectedStatus, setSelectedStatus] = useState(allLabel);
  const ionGridRef                          = useRef<IonGridAPI>();
  const cellMenuRef                         = useRef(null);
  const [rowData, setRowData]               = useState([]);
  const [activeTab, setActiveTab]           = useState(0);
  const [viewDate, setViewDate]             = useState<string>();
  const [healthEventId, setHealthEventId]   = useState<string>();

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(),
    columnState       : null
  });

  const checkDiffDetails = useCallback((HealthEvent) => {
    setviewData(HealthEvent.idReportChanges.diffs);
    setdiffDetails(!diffDetails);
    setViewDate(HealthEvent?.lastUpdatedIDReport);
    setHealthEventId(HealthEvent?.id);
  }, [diffDetails]);

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  const {
    result: {
      healthEvents
    },
    isFetching,
    refresh,
    loading,
    paging
  } = usePaginatedCTCSearch({
    api       : getESNChangeEvents,
    baseKey   : QueryKey.HEALTH_EVENTS,
    pageSize  : 20,
    apiParams : {esn: device?.esn}
  });

  useEffect(() => {
    setRowData(healthEvents);
  }, [healthEvents]);

  const getTypes = async function(eventId?) {
    try {
      const data = await getESNChangeEvents({esn: device?.esn}, eventId);
      setRowData(data.healthEvents);
    } catch (err) {
      setRowData([]);
    }
  };

  const refreshData = useCallback(function() {
    setRefreshGrid(true);
    getTypes();
    setRefreshGrid(false);
  }, [device]);

  const closeMenu = function() {
    if (cellMenuRef.current) {
      cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
    }
  };

  const onShowColumnChooser = useCallback((val) =>
    function() {
      toggleColumnChooser(true);
      closeMenu();
    }, []);

  const onColumnSelectionsUpdate = useCallback((columns: ColDef[]): void => {
    onColumnChooserSave(columns);
  }, []);

  const onColumnChooserClose = useCallback(function(): void {
    toggleColumnChooser(false);
  }, []);

  const handleSearch = useCallback((selectedStatus) => {
    let eventId;
    if (selectedStatus === 'FAILSAFE_ACTION') {
      eventId = 0;
    }
    if (selectedStatus === 'RECOVERY_URL') {
      eventId = 1;
    }
    if (selectedStatus === 'ESN') {
      eventId = 2;
    }
    getTypes(eventId);
  }, [device, selectedStatus]);

  useEffect(() => {
    handleSearch(selectedStatus);
  }, [selectedStatus]);

  function onStatusChange(value) {
    setSelectedStatus(value);
  }

  function onNavigation(e: MouseEvent<HTMLButtonElement>, direction: string) {
    rowData.forEach((row, rowIndex) => {
      if (isEqual(healthEventId, row?.id)) {
        const isForward  = (rowIndex + 1 < rowData.length && isEqual(direction, 'forward'));
        const isBackward = (rowIndex - 1 >= 0 && isEqual(direction, 'back'));
        const {
          id,
          idReportLastUpdated,
          idReportChanges
        } = isForward ? rowData[rowIndex + 1] : rowData[rowIndex - 1];
        if (isForward || isBackward) {
          setHealthEventId(id);
          setViewDate(formatDateTime(idReportLastUpdated));
          setviewData(idReportChanges.diffs);
        }
      }
    });
  }

  return (
    <div id={`detailsTab_${tabIndex}`}>
      {diffDetails ? (
        <>
          <div className={classes.alignStyle}>
            <div className={classes.flexStyle}>
              <div className={classes.dropdownStyle}>
                <EventTypeChooser classes={classes.taskInnerClass}
                  status={selectedStatus}
                  onChange={onStatusChange}/>
              </div>
            </div>
            <div className={classes.actions}>
              <IconButton icon={<ColumnChooserIcon />}
                onClick={onShowColumnChooser(true)} />
              <div className={classes.refresh}>
                <RefreshGridButton onRefresh={refreshData} />
              </div>
            </div>
          </div>
          <IonGrid omitHeader
            ref={ionGridRef}
            loading={isLoading}
            rowData={rowData}
            totalRows={rowData?.length}
            paging={paging}
            isPrefetching={isFetching}
            columnComponents={{
              rowMenu : item => (
                <HealthEventsRowEditor healthEvent={item?.data} diffDetails={checkDiffDetails} />
              ),
              eventTypeCellRenderer,
              diffCountCellRenderer
            }}
            augmentItems={constructHealthEventsGridRows}
            rowModelType={RowModelTypes.ClientSide}
            refreshGrid={refreshGrid}
            columnDefinitions={columnDefinitions}
            defaultDefinitions={defaultDefinitions}
            showColumnChooser={showColumnChooser}
            onColumnChooserSave={onColumnSelectionsUpdate}
            onColumnChooserClose={onColumnChooserClose}
            checkboxesEnabled={false}
            showHeaderCheckbox={false}
            viewSubGroups={false}
            rowMenusEnabled
            paginationEnabled
            showCustomPaginationBar
            pageOffset
            pageSizes={PAGE_SIZES} />
        </>
      ) : (<HealthEventsDiffDetails rowData={viewData}
        selectedTab={activeTab}
        date={viewDate}
        onNavigation={onNavigation}
        tabIndex={9}
        setdiffDetails={setdiffDetails}/>)
      }
    </div>
  );
};

/**
 * Takes a list of HealthEvents data and constructs a list that
 * can be easily consumed by the IonGrid API
 */
export const constructHealthEventsGridRows = (healthEvents): GridResult[] => {
  return healthEvents?.map(event => ({
    ...event,
    lastUpdatedIDReport : `${formatDateTime(event.idReportLastUpdated)}`,
    eventType           : `${event.eventType}`,
    triggerReason       : `${event.triggerReasons}`,
    diffCount           : `${event.diffCount}`
  })) as GridResult[];
};

/**
 * Returns column definitions for HealthEvents grid
 */
const getDefinitions = (): ColDef[] => {
  return [{
    headerName : translate('admin-devices:grid.columns.lastUpdatedIDReport'),
    field      : 'lastUpdatedIDReport',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate('admin-devices:grid.columns.eventType'),
    field        : 'eventType',
    cellRenderer : 'eventTypeCellRenderer',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false
  },
  {
    headerName : translate('admin-devices:grid.columns.triggerReason'),
    field      : 'triggerReason',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate('admin-devices:grid.columns.diffCount'),
    field        : 'diffCount',
    cellRenderer : 'diffCountCellRenderer',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false
  }];
};

const getColumnDefinitions = () => {
  const definitions = getDefinitions();
  return {
    desktop : definitions,
    mobile  : definitions
  };
};

export default HealthEventsTab;
